/**
 * Public chat view component (React Native + React Native Web)
 * Displays shared chat messages based on chatId passed in navigation params
 */
import React, {useEffect, useRef, useState} from 'react';
import {View} from 'react-native';
import {useRoute} from '@react-navigation/native';
import styled from 'styled-components/native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';

import {getChats} from './actions';
import ChatComponent from '../../../components/chat';
import type {ChatScreenRef} from '../../../interfaces/components/chatComponent';
import TextGeneric from '../../../components/textGeneric';

// Styled Components
const Container = styled.SafeAreaView`
  flex: 1;
  background-color: ${({theme}) => theme.screenBackground};
`;

const HeaderBar = styled.View`
  width: 100%;
  padding: ${({theme}) => theme.text.s7}px ${({theme}) => theme.text.s10}px
    ${({theme}) => theme.text.s6}px;
  border-bottom-width: 1px;
  border-bottom-color: ${({theme}) => theme.text.grey + '22'};
  background-color: ${({theme}) => theme.header.background};
`;

const Title = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s4}px;
  color: ${({theme}) => theme.header.primary};
`;

const Subtitle = styled(TextGeneric)`
  margin-top: ${({theme}) => theme.text.s10 / 2}px;
  color: ${({theme}) => theme.text.grey};
`;

const Chats: React.FC = () => {
  const route = useRoute<any>(); // or useRoute<{ params: { chatId: string } }>();
  const {chatId} = route.params;

  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [metadata, setMetadata] = useState<any>(undefined);
  const flatListRef = useRef<ChatScreenRef>(null);
  const insets = useSafeAreaInsets();

  const getData = async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const {messages, metadata} = await getChats(chatId);
      setData(messages);
      setMetadata(metadata);
      setTimeout(() => flatListRef.current?.scrollToEnd(false), 50);
    } catch (error) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const messageCount = data?.length || 0;

  return (
    <Container style={{paddingTop: Math.max(insets.top, 0)}}>
      <HeaderBar>
        <Title bold numberOfLines={1}>Shared Chat</Title>
       {!isLoading && ( <Subtitle numberOfLines={1}>{messageCount} message{messageCount === 1 ? '' : 's'}</Subtitle>)}
      </HeaderBar>
      <View style={{flex: 1}}>
        <ChatComponent
          //@ts-ignore
          ref={flatListRef}
          userId={''}
          userName={''}
          data={data}
          isWriting={false}
          isLoading={isLoading && data.length === 0}
          isLoadingMore={isLoading && data.length > 0}
          isError={isError}
          isMessageSendingError={false}
          isMessageSendingLoading={false}
          streamMessageLoadingId={undefined}
          onMessageTryAgain={() => {}}
          onRefresh={getData}
          onStreamingMessageEndAnimation={() => {}}
          onlineUsers={[]}
          onCheckAnswerPressed={() => {}}
          question={''}
          setQuestion={() => {}}
          onRecordHandler={() => {}}
          isRecording={false}
          isErrorAnswer={false}
          introQuestions={[]}
          onIntroQuestionsRefreshPress={() => {}}
          selectedRecommendationTab={undefined}
          onRecommendationClosePress={() => {}}
          isQNA
          onQuestionPress={() => {}}
          references={[]}
          metadata={metadata}
          layoutDirection={'ltr'}
          onSharePress={() => {}}
          onAddToFavorite={() => {}}
          isFavorite={false}
          shared
          onReadTextPress={() => {}}
          isSpeaking={false}
          fullHeight
        />
      </View>
    </Container>
  );
};

export default Chats;
